/**
 * <details-table-toggle>
 *
 * Row-based truncation for the details table. The full set of rows stays in
 * the DOM; when there are more rows than the cutoff, the rows container is
 * clipped to the bottom of the last visible row, so the first X rows stay
 * fully visible and every row past the cutoff is hidden entirely. An expand /
 * collapse tertiary button animates max-height and swaps between the keyboard
 * down / up material icons.
 */

class DetailsTableToggle extends HTMLElement {
  #container = null;
  #trigger = null;
  #label = null;
  #icon = null;
  #rowCount = 5;
  #expandLabel = 'Show all';
  #collapseLabel = 'Show less';
  #collapsedHeight = 0;
  #active = false;
  #resizeRaf = 0;

  connectedCallback() {
    this.#container = this.querySelector('[data-rows-container]');
    this.#trigger = this.querySelector('[data-trigger]');
    this.#label = this.querySelector('[data-label]');
    this.#icon = this.querySelector('.details-table-toggle__icon');
    if (!this.#container || !this.#trigger || !this.#label) return;

    this.#rowCount = parseInt(this.dataset.rows || '', 10) || 5;
    this.#expandLabel = this.#trigger.dataset.expandLabel || this.#expandLabel;
    this.#collapseLabel = this.#trigger.dataset.collapseLabel || this.#collapseLabel;

    this.#trigger.addEventListener('click', this.#onClick);
    window.addEventListener('resize', this.#onResize);

    // Measure after the first layout/paint so row heights are settled.
    requestAnimationFrame(() => this.#setup());
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onClick);
    window.removeEventListener('resize', this.#onResize);
    cancelAnimationFrame(this.#resizeRaf);
  }

  get #expanded() {
    return this.dataset.state === 'expanded';
  }

  #rows() {
    return Array.from(this.#container.querySelectorAll('.details-row'));
  }

  #onClick = () => {
    if (this.#expanded) this.#collapse();
    else this.#expand();
  };

  /**
   * Collapsed geometry: clip exactly to the bottom of the last visible row so
   * every row past the cutoff is hidden entirely. Returns null when there's
   * nothing hidden beyond the cutoff.
   * @returns {{ collapsedHeight: number } | null}
   */
  #measure() {
    const rows = this.#rows();
    if (rows.length <= this.#rowCount) return null;

    const containerTop = this.#container.getBoundingClientRect().top;
    const lastVisible = rows[this.#rowCount - 1]; // last fully-shown row
    const collapsedHeight = Math.ceil(lastVisible.getBoundingClientRect().bottom - containerTop);
    return { collapsedHeight };
  }

  #setup() {
    if (this.#expanded) return;

    const measured = this.#measure();
    if (measured == null) {
      this.#teardown();
      return;
    }

    this.#collapsedHeight = measured.collapsedHeight;
    this.#active = true;
    // none → px is not animatable, so the initial clamp applies instantly.
    this.#container.classList.add('is-collapsible');
    this.#container.style.maxHeight = `${measured.collapsedHeight}px`;
    this.#label.textContent = this.#expandLabel;
    if (this.#icon) this.#icon.textContent = 'keyboard_arrow_down';
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#trigger.hidden = false;
    this.dataset.state = 'collapsed';
  }

  #teardown() {
    this.#active = false;
    this.#container.classList.remove('is-collapsible');
    this.#container.style.maxHeight = '';
    this.#trigger.hidden = true;
    this.dataset.state = '';
  }

  #onResize = () => {
    // Only re-measure while collapsed — width changes the row→height mapping.
    if (!this.#active || this.#expanded) return;
    cancelAnimationFrame(this.#resizeRaf);
    this.#resizeRaf = requestAnimationFrame(() => {
      this.#container.style.maxHeight = '';
      const measured = this.#measure();
      if (measured == null) {
        this.#teardown();
        return;
      }
      this.#collapsedHeight = measured.collapsedHeight;
      this.#container.style.maxHeight = `${measured.collapsedHeight}px`;
    });
  };

  #expand() {
    const container = this.#container;
    this.dataset.state = 'expanded';
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#label.textContent = this.#collapseLabel;
    if (this.#icon) this.#icon.textContent = 'keyboard_arrow_up';
    container.style.maxHeight = `${container.scrollHeight}px`;

    const onEnd = (event) => {
      if (event.target !== container || event.propertyName !== 'max-height') return;
      // Drop the fixed height so the table can reflow freely while open.
      container.style.maxHeight = 'none';
      container.removeEventListener('transitionend', onEnd);
    };
    container.addEventListener('transitionend', onEnd);
  }

  #collapse() {
    const container = this.#container;
    // Establish an explicit start height (none → px doesn't animate).
    container.style.maxHeight = `${container.scrollHeight}px`;
    void container.offsetHeight; // force reflow
    requestAnimationFrame(() => {
      container.style.maxHeight = `${this.#collapsedHeight}px`;
    });

    this.dataset.state = 'collapsed';
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#label.textContent = this.#expandLabel;
    if (this.#icon) this.#icon.textContent = 'keyboard_arrow_down';
  }
}

if (!customElements.get('details-table-toggle')) {
  customElements.define('details-table-toggle', DetailsTableToggle);
}
